/**
 * Entry point of Quadratic Gallery (二次函数图鉴): boots the PixiJS v8
 * application, wires the problem registry (tab bar) and the 小问 scene
 * tabs to the Cartesian board and the DOM sidebar, and exposes the
 * `window.__quadGallery` QA debug surface.
 */

import { Application } from "pixi.js";

import { PROBLEMS, problemById } from "./problems";
import { Board } from "./render/board";
import { QuadPanel } from "./ui";
import type { Pt } from "./math/func";
import type { ProblemDef } from "./problems/types";

/** Debug surface used by the automated QA scripts (and browser devtools). */
interface QuadGalleryDebug {
  /** True once the app booted and the board is painted. */
  readonly ready: boolean;
  /** Id of the currently displayed problem. */
  readonly problemId: string;
  /** Ids of all registered problems, in tab order. */
  readonly problems: readonly string[];
  /** Index of the currently displayed 小问 scene. */
  readonly scene: number;
  /** Number of scenes of the current problem. */
  readonly sceneCount: number;
  /** Switch the displayed problem (rebuilds board and sidebar). */
  loadProblem(id: string): void;
  /** Switch the displayed 小问 scene of the current problem. */
  showScene(i: number): void;
  /** World-coordinate table of the current scene's named points. */
  points(): Record<string, Pt>;
  /** Key derived numbers of the current problem (see each problem's tests). */
  readonly computed: Readonly<Record<string, number>>;
  /** Open the solution card of the current problem and reveal the answer. */
  revealAnswer(): void;
}

declare global {
  interface Window {
    __quadGallery?: QuadGalleryDebug;
  }
}

/** Cap the canvas pixel ratio like the other pages. */
const MAX_DEVICE_PIXEL_RATIO = 2;

/**
 * Boots the PixiJS v8 application and assembles the read-only gallery page:
 * problem registry -> scene data -> Cartesian board -> sidebar panel.
 */
async function main(): Promise<void> {
  const stageHost = document.getElementById("stage");
  const panelHost = document.getElementById("panel");
  if (stageHost === null || panelHost === null) {
    throw new Error("#stage / #panel host elements not found");
  }

  const app = new Application();
  await app.init({
    background: "#0a0e17",
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO),
    autoDensity: true,
    resizeTo: stageHost,
  });
  stageHost.appendChild(app.canvas);

  const board = new Board(app);

  const first = PROBLEMS[0];
  if (first === undefined) {
    throw new Error("no quad-gallery problems registered");
  }

  /** Live state of the displayed problem and 小问 scene. */
  const current: { problem: ProblemDef; sceneIndex: number } = {
    problem: first,
    sceneIndex: 0,
  };

  const panel = new QuadPanel(panelHost, PROBLEMS, current.problem, {
    onSelectScene: (index) => {
      const scene = current.problem.scenes[index];
      if (scene === undefined) {
        return;
      }
      current.sceneIndex = index;
      board.show(scene);
    },
    onSelectProblem: (id) => {
      loadProblem(id);
    },
  });

  /** Swap in another problem: board repaint + sidebar rebuild, scene reset. */
  function loadProblem(id: string): void {
    const problem = problemById(id);
    if (problem === undefined) {
      return;
    }
    current.problem = problem;
    current.sceneIndex = 0;
    const scene = problem.scenes[0];
    if (scene !== undefined) {
      board.show(scene);
    }
    panel.showProblem(problem);
  }

  const firstScene = current.problem.scenes[0];
  if (firstScene !== undefined) {
    board.show(firstScene);
  }

  const relayout = (): void => {
    board.relayout(app.screen.width, app.screen.height);
  };
  app.renderer.on("resize", relayout);
  relayout();

  window.__quadGallery = {
    ready: true,
    get problemId() {
      return current.problem.id;
    },
    problems: PROBLEMS.map((p) => p.id),
    get scene() {
      return current.sceneIndex;
    },
    get sceneCount() {
      return current.problem.scenes.length;
    },
    loadProblem,
    showScene: (i) => {
      panel.showScene(i);
    },
    points: () => {
      const out: Record<string, Pt> = {};
      const scene = current.problem.scenes[current.sceneIndex];
      for (const point of scene?.points ?? []) {
        out[point.name] = { x: point.at.x, y: point.at.y };
      }
      return out;
    },
    get computed() {
      return current.problem.computed;
    },
    revealAnswer: () => {
      panel.revealAnswer();
    },
  };
}

void main();
